import { useRef, useState } from 'react';

import { notify } from '../../shared';
import { Button, SubmitButton, Loading } from '../../components';
import { Modal, Input } from '../../components';
import { getErrorMessage } from '../../utilities';
import { useRequest } from '../../hooks';
import { importCampaign } from '../../api';

type ImportModalProps = {
  onClose: () => void;
  onSuccess?: () => void;
};

const ImportModal: React.FC<ImportModalProps> = ({ onClose, onSuccess }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [jsonText, setJsonText] = useState<string>('');

  const [handleImport, loading] = useRequest(
    (_signal, payload: any) => {
      return importCampaign(payload);
    },
    {
      deps: [],
      onSuccess: () => {
        notify.success('Campaign imported successfully');
        onSuccess?.();
        onClose();
      },
      onError: ex => {
        notify.error(getErrorMessage(ex, 'Failed to import campaign'));
      },
    }
  );

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setSelectedFile(file);
    setJsonText('');
  };

  const handleFileUpload = async () => {
    if (!selectedFile) return;

    try {
      const text = await selectedFile.text();
      const payload = JSON.parse(text);
      handleImport(payload);
    } catch (err) {
      notify.error('Invalid JSON file');
    }
  };

  const handleJsonImport = () => {
    if (!jsonText.trim()) {
      notify.error('Please enter campaign JSON');
      return;
    }

    try {
      const payload = JSON.parse(jsonText);
      handleImport(payload);
    } catch {
      notify.error('Invalid JSON format');
    }
  };

  return (
    <Modal
      open
      size="big"
      title="Import Campaign"
      onClose={onClose}
      bottomActions={
        <>
          <Button onClick={onClose}>Cancel</Button>
        </>
      }
    >
      <div className="flex flex-col gap-y-lg">
        <div className="text-small text-text-muted">
          Import a campaign from an exported JSON file or paste JSON directly
        </div>

        <div className="flex flex-col gap-y-md">
          <div className="text-small font-semibold">File Upload</div>
          <div className="flex flex-row flex-wrap gap-md items-center">
            <input
              ref={fileInputRef}
              type="file"
              accept=".json"
              onChange={handleFileChange}
              className="text-small text-text file:mr-md file:py-sm file:px-lg file:rounded-sm file:border file:border-active file:bg-content file:text-text file:font-medium file:cursor-pointer hover:file:bg-active"
            />
            <SubmitButton
              type="primary"
              loading={loading}
              disabled={!selectedFile}
              onClick={handleFileUpload}
            >
              Upload
            </SubmitButton>
          </div>
          {selectedFile && (
            <span className="text-small text-text-muted">
              Selected: {selectedFile.name}
            </span>
          )}
        </div>

        <div className="flex flex-col gap-y-md">
          <div className="text-small font-semibold">Or Paste JSON</div>
          <Input
            multiline
            rows={12}
            placeholder="Paste campaign JSON export here..."
            value={jsonText}
            onChange={value => {
              setJsonText(value);
              setSelectedFile(null);
            }}
          />
          <SubmitButton
            type="primary"
            loading={loading}
            disabled={!jsonText.trim()}
            onClick={handleJsonImport}
          >
            Import from JSON
          </SubmitButton>
        </div>
      </div>
      {loading && <Loading />}
    </Modal>
  );
};

export default ImportModal;
